// Percentile stat structure (value + percentile ranking)
export interface PercentileStat {
  value: number | null;
  percentile: number | null;
}

// Batted ball profile for a single season
export interface BattedBallProfile {
  bbe: number;
  gb_pct: number | null;
  fb_pct: number | null;
  ld_pct: number | null;
  popup_pct: number | null;
  pull_pct: number | null;
}

// Contact quality metrics for a single season
export interface ContactQuality {
  bbe: number;
  avg_ev: number | null;
  max_ev: number | null;
  hard_hit_pct: number | null;
  sweet_spot_pct: number | null;
  barrel_pct: number | null;
}

// Batter season percentiles
export interface BatterSeasonPercentiles {
  k_pct: PercentileStat;
  bb_pct: PercentileStat;
  xwoba: PercentileStat;
  barrel_pct: PercentileStat;
  ev: PercentileStat;
  chase_pct: PercentileStat;
}

// Player list item (shared structure for batter/pitcher lists)
export interface PlayerListItem {
  mlb_id: number;
  name: string;
  team: string | null;
  is_active: boolean;
  last_season: number;
}

// Batter-specific list item
export interface BatterListItem extends PlayerListItem {
  bats: string | null;
}

// Pitcher-specific list item
export interface PitcherListItem extends PlayerListItem {
  throws: string | null;
}

// Player info structure (shared base)
export interface PlayerInfo {
  mlb_id: number;
  full_name: string;
  position: string | null;
  team: string | null;
  bats: string | null;
  throws: string | null;
  age: number | null;
  birth_date: string | null;
  is_active: boolean;
  on_active_roster: boolean | null;
  seasons: number[];
}

// Pitch type labels mapping
export type PitchTypeLabels = Record<string, string>;

// Default pitch types order
export const DEFAULT_PITCH_TYPES = ['ff', 'si', 'fc', 'fs', 'sl', 'cu', 'sw', 'ch', 'other'];

// React Query stale time constants (in milliseconds)
export const STALE_TIMES = {
  // Frequently changing data
  LIVE_DATA: 5 * 60 * 1000, // 5 minutes for live/in-progress data
  SCHEDULE: 10 * 60 * 1000, // 10 minutes for schedules
  CURRENT_SEASON: 15 * 60 * 1000, // 15 minutes for current season data

  // Moderately changing data
  CAREER_STATS: 30 * 60 * 1000, // 30 minutes for career stats
  PLAYER_INFO: 60 * 60 * 1000, // 1 hour for player info
  TEAM_STATS: 60 * 60 * 1000, // 1 hour for team stats

  // Slowly changing data
  HOT_COLD_PLAYERS: 2 * 60 * 60 * 1000, // 2 hours for hot/cold player lists

  // Rarely changing data
  PLAYER_LIST: 24 * 60 * 60 * 1000, // 24 hours for player lists (rarely changes)
  HISTORICAL_DATA: 24 * 60 * 60 * 1000, // 24 hours for historical/franchise data
} as const;

// React Query retry configuration
export const QUERY_RETRY_CONFIG = {
  retry: 2,
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
} as const;
